// src/components/inbox/InboxPanel.js
//
// Pass 58F3 — the ONE canonical Inbox. An embeddable utility (no header of its own — the global header
// and the workspace nav are provided by the shell it renders inside). Two honest tabs: Notifications
// and Messages, each a list + detail. All data is the signed-in user's OWN, canonically scoped:
//   · notifications: `notifications` where userId == auth uid (recipient-only; rules enforce it)
//   · messages: `threads` where participantIds array-contains auth uid (participant-only; rules enforce it)
// No fabricated activity, no fake counts, no dead CTA. Empty states are intentional.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../AuthContext';
import { useNotifications } from '../../hooks/useNotifications';
import { useInboxMode } from '../../hooks/useInboxMode';
import { useStudentThreads, useStudentNotifications } from '../../hooks/useStudentComms';
import { getNotificationPresentation, markNotificationRead } from '../../utils/notifications';
import { sendThreadMessage, markThreadRead, threadUnreadFor, unreadThreadCount } from '../../utils/reviewMessaging';
import MotusIcon from '../shared/MotusIcon';

// A stable per-send message id (retry-safe canonical send; no blind addDoc). Uses crypto.randomUUID when
// available, else a time+random fallback — the value only needs to be unique per UI send attempt.
function newMessageDraftId() {
  try { if (typeof crypto !== 'undefined' && crypto.randomUUID) return `m_${crypto.randomUUID()}`; } catch { /* */ }
  return `m_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

const C = {
  bg:'#FAFAF8', card:'#FFFFFF', card2:'#F5F4F0', border:'rgba(0,0,0,0.08)', borderH:'rgba(0,0,0,0.14)',
  blue:'#2563EB', blueDim:'rgba(37,99,235,0.08)', green:'#16A34A', red:'#DC2626',
  t1:'#1C1B18', t2:'#57564F', t3:'#8A8980',
};
const font = "'DM Sans',system-ui,sans-serif";
const fontD = "'Bebas Neue',sans-serif";

const tsMs = (v) => { try { if (!v) return null; if (v.toDate) return v.toDate().getTime(); if (v.seconds) return v.seconds * 1000; if (v._seconds) return v._seconds * 1000; const d = new Date(v); return Number.isNaN(d.getTime()) ? null : d.getTime(); } catch { return null; } };
const fmtTime = (v) => { const m = tsMs(v); if (!m) return ''; try { return new Date(m).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return new Date(m).toLocaleString(); } };

export default function InboxPanel() {
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth() || {};
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const tab = rawTab === 'messages' ? 'messages' : 'notifications';

  const uid = currentUser?.uid || '';

  // Pass 85.1B.1 (§21/§22) — the inbox mode is the ACTIVE WORKSPACE's communication contract (shared with the header via
  // useInboxMode), NOT the mere possession of a staff capability. Personal ("My Coaching") reads as a STUDENT through the
  // canonical ACCESS LANE (participant + selected org); a MODERN lane yields scope-bound hooks (a context switch
  // suppresses the prior scope synchronously and the unread total is tenant-safe), and every unresolved/legacy lane
  // performs ZERO reads and shows an honest state. Instructor/Operations/Admin read as STAFF with an ORG-SCOPED query the
  // hardened rules can prove. Student Preview surfaces NO inbox. A dual-role human is a student in Personal, staff in
  // Instructor/Operations — the workspace decides, never a leaked capability.
  const { mode, lane, studentOrg, studentPid, staffOrg, staffReadsThreads } = useInboxMode();
  const studentMode = mode === 'student' && lane === 'modern';
  const staffMode = mode === 'staff' && !!staffOrg;

  // STAFF notifications: org-scoped (rules require current org staff), disabled unless staffMode; non-participant only.
  const staffNotifications = useNotifications({ enabled: staffMode, organizationId: staffOrg });
  const sn = useStudentNotifications(studentMode ? uid : '', studentOrg, studentPid);
  const st = useStudentThreads(studentMode ? uid : '', studentOrg, studentPid);

  const [selectedNotifId, setSelectedNotifId] = useState(null);
  // §15 — the selected thread is the URL's ?thread=<id> (single source of truth → survives refresh/back).
  const selectedThreadId = searchParams.get('thread') || null;
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [mobileDetail, setMobileDetail] = useState(false); // 390px: list ⇄ detail
  const draftIdRef = useRef(newMessageDraftId()); // stable id per in-flight send attempt (retry-safe)

  // STAFF threads (§20/§31): organizationId == authorised org AND instructorId == uid (the caller is the review thread's
  // reviewer). This is an EQUALITY-only query — an array-contains filter is incompatible with the get()-bearing thread
  // read rule under `list` (the whole query is denied), whereas equality filters let the rule prove each row. Runs ONLY
  // in staff mode with a resolved org. (A staff member is always the thread's instructorId; their own STUDENT threads,
  // where they are the studentId, belong to their Personal workspace, not here.)
  const [staffThreadState, setStaffThreadState] = useState({ scopeKey: '', list: [] });
  // §4/§6 — issue the coaching-thread query ONLY for a teaching account; a pure operator (ops/non-teaching owner/admin) has
  // no coaching-thread authority (a pure-ops list is rules-denied), so it reads NO threads and shows "no conversations".
  const staffThreadScope = staffMode && staffReadsThreads ? `${uid}::${staffOrg}` : '';
  useEffect(() => {
    setStaffThreadState({ scopeKey: staffThreadScope, list: [] });
    if (!staffThreadScope) return undefined;
    const q = query(collection(db, 'threads'), where('organizationId', '==', staffOrg), where('instructorId', '==', uid));
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (tsMs(b.lastMessageTimestamp) || 0) - (tsMs(a.lastMessageTimestamp) || 0));
      setStaffThreadState({ scopeKey: staffThreadScope, list });
    }, () => setStaffThreadState({ scopeKey: staffThreadScope, list: [] }));
    return () => { if (typeof unsub === 'function') unsub(); };
  }, [staffThreadScope, staffOrg, uid]);
  // Synchronous stale-scope suppression (mirrors useStudentComms): a workspace/org change never shows prior rows.
  const staffThreads = useMemo(() => (staffThreadState.scopeKey === staffThreadScope ? staffThreadState.list : []), [staffThreadState, staffThreadScope]);

  // The active data set + honest lane state. In studentMode data comes from the scope-bound hooks; in staffMode from the
  // org-scoped staff query (NON-participant notifications only — a dual-role account's participant rows never bleed into
  // the staff inbox). Every other mode/lane yields NO data and an explicit state message.
  const notifications = useMemo(() => (studentMode ? sn.notifications : (staffMode ? staffNotifications.filter((n) => !n.participantId) : [])), [studentMode, staffMode, sn.notifications, staffNotifications]);
  const threads = useMemo(() => (studentMode ? st.threads : (staffMode ? staffThreads : [])), [studentMode, staffMode, st.threads, staffThreads]);
  const unreadNotifications = useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);
  const unreadMessages = useMemo(() => unreadThreadCount(threads, uid), [threads, uid]);
  // Honest state banner: student lanes that are not (yet) a modern inbox, or a Student Preview / no-comms surface.
  const laneState = studentMode || staffMode ? '' : (
    mode === 'none' ? 'preview'
      : lane === 'loading' ? 'loading'
      : lane === 'error' ? 'error'
      : lane === 'needs_org' ? 'needs_org'
      : lane === 'ambiguous' ? 'ambiguous'
      : lane === 'legacy' ? 'legacy'
      : mode === 'staff' ? 'no_org' // staff workspace without a resolved org → fail closed, no reads
      : ''
  );

  // §8/§9 — the selected thread must be a member of the CURRENT authorised thread set before ANY child message read.
  // A deep-link ?thread=<id> for a thread that is not in the authorised result (wrong scope / unavailable / unresolved
  // lane) starts NO child listener; a context switch (A→B) drops T_A from the set → the effect below unsubscribes and
  // clears messages, and a late T_A snapshot can never repaint.
  const authorizedThread = useMemo(() => threads.find((t) => t.id === selectedThreadId) || null, [threads, selectedThreadId]);

  // Messages for the AUTHORISED selected thread only (single-field orderBy — no composite index).
  useEffect(() => {
    if (!selectedThreadId || !authorizedThread) { setMessages([]); return undefined; }
    const q = query(collection(db, 'threads', selectedThreadId, 'messages'), orderBy('timestamp'));
    const unsub = onSnapshot(q, (snap) => setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() }))), () => setMessages([]));
    return () => unsub();
  }, [selectedThreadId, authorizedThread]);

  const setTab = (next) => { const p = new URLSearchParams(searchParams); p.set('tab', next); if (next !== 'messages') p.delete('thread'); setSearchParams(p, { replace: true }); setMobileDetail(false); };

  const selectedNotif = notifications.find((n) => n.id === selectedNotifId) || null;
  const selectedThread = authorizedThread; // §9 — only an AUTHORISED thread is ever the detail/target

  const openNotification = (n) => {
    setSelectedNotifId(n.id); setMobileDetail(true);
    // Real product semantics: opening an unread notification marks it read (recipient-only update).
    if (n && !n.read && n.id) { Promise.resolve(markNotificationRead(db, n.id)).catch(() => {}); }
  };
  // §15/§23 — selecting a conversation deep-links it (?thread=) and clears the caller's OWN unread bit.
  const openThread = (t) => {
    const p = new URLSearchParams(searchParams); p.set('tab', 'messages'); p.set('thread', t.id); setSearchParams(p, { replace: true });
    setMobileDetail(true);
    if (threadUnreadFor(t, uid)) { Promise.resolve(markThreadRead(db, t.id, uid)).catch(() => {}); }
  };

  // §23 — a deep-linked / refreshed thread that is unread for the caller is marked read once it loads.
  useEffect(() => {
    if (!selectedThread || !uid) return;
    if (threadUnreadFor(selectedThread, uid)) { Promise.resolve(markThreadRead(db, selectedThread.id, uid)).catch(() => {}); }
  }, [selectedThread, uid]);

  const threadTitle = (t) => t.title || t.instructorName || (Array.isArray(t.participantNames) ? t.participantNames.find((n) => n) : '') || 'Conversation';

  // §17/§18/§19 — the ONE canonical send: retry-safe stable message id → one message + one thread update + one
  // create-once new_message notification + recipient unread bit, all via sendThreadMessage. The draft id is
  // retained until a definite success, so a transient error retries the SAME document id (no duplicates).
  const send = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || !selectedThread || !uid) return;
    setDraft('');
    try {
      await sendThreadMessage(db, { threadId: selectedThread.id, senderUid: uid, senderName: userData?.name || '', text, messageId: draftIdRef.current });
      draftIdRef.current = newMessageDraftId(); // success → fresh id for the next message
    } catch { setDraft(text); } // keep the SAME draftIdRef so a retry reuses the id
  };

  return (
    <div style={{ fontFamily: font, color: C.t1 }}>
      <style>{`
        .inbox-wrap{display:grid;grid-template-columns:340px 1fr;gap:16px;align-items:start}
        .inbox-detail-mobileback{display:none}
        @media (max-width:760px){
          .inbox-wrap{grid-template-columns:1fr}
          .inbox-list{display:${mobileDetail ? 'none' : 'block'}}
          .inbox-detail{display:${mobileDetail ? 'block' : 'none'}}
          .inbox-detail-mobileback{display:inline-flex}
        }
      `}</style>

      {/* Inbox header + tabs (utility header — NOT a second app header) */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontFamily: fontD, fontSize: 26, letterSpacing: 1, color: C.t1, marginBottom: 10 }}>Inbox</div>
        <div style={{ display: 'inline-flex', gap: 2, background: C.card, border: `1px solid ${C.border}`, borderRadius: 11, padding: 3 }}>
          <TabBtn active={tab === 'notifications'} onClick={() => setTab('notifications')} label="Notifications" count={unreadNotifications} />
          <TabBtn active={tab === 'messages'} onClick={() => setTab('messages')} label="Messages" count={unreadMessages} />
        </div>
      </div>

      {laneState ? (
        <div data-testid="inbox-lane-state" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 26, fontSize: 13.5, color: laneState === 'error' ? C.red : C.t2, maxWidth: 620 }}>
          {laneState === 'loading' ? 'Resolving your coaching profile…'
            : laneState === 'error' ? 'We couldn’t load your coaching identity. Please try again — your inbox is not empty, it just couldn’t load.'
            : laneState === 'needs_org' ? 'Choose your organisation to see its messages and notifications.'
            : laneState === 'ambiguous' ? 'Your account is linked to more than one profile in this organisation — your coach must resolve this before your inbox can load.'
            : laneState === 'preview' ? 'Student Preview does not open a student’s private inbox. Switch to your own workspace to read your messages and notifications.'
            : laneState === 'no_org' ? 'Select an organisation workspace to see its messages and notifications.'
            : 'Your messages and notifications appear here once your coaching organisation links a participant profile to your account.'}
        </div>
      ) : (
      <div className="inbox-wrap">
        {/* LEFT — list */}
        <div className="inbox-list" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: 'hidden' }}>
          {tab === 'notifications' ? (
            notifications.length === 0 ? (
              <EmptyState icon="bell" title="You're all caught up" lines={["No new notifications.", "We'll surface schedule updates and items that need your attention here."]} />
            ) : (
              notifications.map((n) => {
                const p = getNotificationPresentation(n);
                return (
                  <Row key={n.id} active={selectedNotifId === n.id} unread={!n.read} onClick={() => openNotification(n)}
                    title={p.title} preview={p.message} time={fmtTime(n.createdAt)} />
                );
              })
            )
          ) : (
            threads.length === 0 ? (
              <EmptyState icon="message" title="No conversations yet" lines={["Your conversations will appear here."]} />
            ) : (
              threads.map((t) => (
                <Row key={t.id} active={selectedThreadId === t.id} unread={threadUnreadFor(t, uid)} onClick={() => openThread(t)}
                  title={threadTitle(t)} preview={t.lastMessage || 'No messages yet'} time={fmtTime(t.lastMessageTimestamp)} />
              ))
            )
          )}
        </div>

        {/* RIGHT — detail */}
        <div className="inbox-detail" style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, minHeight: 320, display: 'flex', flexDirection: 'column' }}>
          <button type="button" onClick={() => setMobileDetail(false)} className="inbox-detail-mobileback"
            style={{ alignItems: 'center', gap: 6, margin: 12, background: C.card2, border: `1px solid ${C.border}`, borderRadius: 8, padding: '6px 10px', fontSize: 12.5, fontWeight: 600, color: C.t2, cursor: 'pointer', fontFamily: font, width: 'fit-content' }}>
            <MotusIcon name="arrowLeft" size={14} color={C.t2} /> Back to inbox
          </button>

          {tab === 'notifications' ? (
            selectedNotif ? (
              <NotificationDetail notif={selectedNotif} onAction={(link) => navigate(link)} />
            ) : (
              <NeutralDetail text="Select a notification to see its details." />
            )
          ) : (
            selectedThread ? (
              <ThreadDetail title={threadTitle(selectedThread)} messages={messages} currentUid={currentUser?.uid} draft={draft} setDraft={setDraft} onSend={send} />
            ) : (
              <NeutralDetail text="Select a conversation to read it." />
            )
          )}
        </div>
      </div>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, label, count }) {
  return (
    <button onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 15px', borderRadius: 8, border: 'none', cursor: 'pointer', fontFamily: font, fontSize: 13.5, fontWeight: active ? 700 : 500, color: active ? '#fff' : C.t2, background: active ? C.blue : 'transparent' }}>
      {label}
      {count > 0 && <span style={{ background: active ? 'rgba(255,255,255,0.25)' : C.red, color: '#fff', borderRadius: 9, fontSize: 10.5, fontWeight: 800, padding: '1px 6px', minWidth: 16, textAlign: 'center' }}>{count > 9 ? '9+' : count}</span>}
    </button>
  );
}

function Row({ active, unread, onClick, title, preview, time }) {
  return (
    <button onClick={onClick} style={{ width: '100%', textAlign: 'left', display: 'block', background: active ? C.blueDim : 'transparent', border: 'none', borderBottom: `1px solid ${C.border}`, padding: '13px 15px', cursor: 'pointer', fontFamily: font }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {unread && <span style={{ width: 8, height: 8, borderRadius: '50%', background: C.blue, flexShrink: 0 }} />}
        <span style={{ fontSize: 14, fontWeight: unread ? 800 : 600, color: C.t1, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
        {time && <span style={{ fontSize: 11, color: C.t3, flexShrink: 0 }}>{time}</span>}
      </div>
      {preview && <div style={{ fontSize: 12.5, color: C.t3, marginTop: 3, marginLeft: unread ? 16 : 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{preview}</div>}
    </button>
  );
}

function EmptyState({ icon, title, lines }) {
  return (
    <div style={{ padding: '48px 26px', textAlign: 'center' }}>
      <div style={{ width: 46, height: 46, borderRadius: 12, background: C.card2, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
        <MotusIcon name={icon} size={22} color={C.t3} />
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: C.t1, marginBottom: 6 }}>{title}</div>
      {lines.map((l, i) => <div key={i} style={{ fontSize: 13, color: C.t3, lineHeight: 1.5 }}>{l}</div>)}
    </div>
  );
}

function NeutralDetail({ text }) {
  return <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 30, color: C.t3, fontSize: 13.5, textAlign: 'center' }}>{text}</div>;
}

function NotificationDetail({ notif, onAction }) {
  const p = getNotificationPresentation(notif);
  return (
    <div style={{ padding: 22 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
        <div style={{ fontSize: 17, fontWeight: 800, color: C.t1 }}>{p.title}</div>
        {!notif.read && <span style={{ background: C.blue, color: '#fff', borderRadius: 20, fontSize: 10.5, fontWeight: 800, padding: '2px 9px' }}>New</span>}
      </div>
      <div style={{ fontSize: 14, color: C.t2, lineHeight: 1.6 }}>{p.message}</div>
      {fmtTime(notif.createdAt) && <div style={{ fontSize: 12, color: C.t3, marginTop: 12 }}>{fmtTime(notif.createdAt)}</div>}
      {notif.link && (
        <button type="button" onClick={() => onAction(notif.link)} style={{ marginTop: 18, background: C.blue, color: '#fff', border: 'none', borderRadius: 9, padding: '9px 18px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', fontFamily: font }}>
          {p.ctaLabel || 'Open'}
        </button>
      )}
    </div>
  );
}

function ThreadDetail({ title, messages, currentUid, draft, setDraft, onSend }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 320 }}>
      <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}`, fontSize: 15, fontWeight: 700, color: C.t1 }}>{title}</div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {messages.length === 0 ? (
          <div style={{ color: C.t3, fontSize: 13, margin: 'auto' }}>No messages in this conversation yet.</div>
        ) : messages.map((m) => {
          const mine = m.userId === currentUid;
          return (
            <div key={m.id} style={{ maxWidth: '78%', alignSelf: mine ? 'flex-end' : 'flex-start', background: mine ? C.blue : C.card2, color: mine ? '#fff' : C.t1, borderRadius: 12, padding: '9px 13px', fontSize: 13.5, lineHeight: 1.45 }}>
              {m.message}
              <div style={{ fontSize: 10.5, color: mine ? 'rgba(255,255,255,0.75)' : C.t3, marginTop: 4, textAlign: 'right' }}>{fmtTime(m.timestamp)}</div>
            </div>
          );
        })}
      </div>
      <form onSubmit={onSend} style={{ borderTop: `1px solid ${C.border}`, padding: 12, display: 'flex', gap: 8 }}>
        <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Write a message…"
          style={{ flex: 1, border: `1px solid ${C.border}`, borderRadius: 9, padding: '9px 12px', fontSize: 13.5, fontFamily: font, outline: 'none' }} />
        <button type="submit" disabled={!draft.trim()} style={{ background: draft.trim() ? C.blue : '#9DB4F0', color: '#fff', border: 'none', borderRadius: 9, padding: '9px 16px', fontSize: 13.5, fontWeight: 700, cursor: draft.trim() ? 'pointer' : 'default', fontFamily: font }}>Send</button>
      </form>
    </div>
  );
}
